import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import type { GameFilter } from '../../../shared/models/GameModels';
import { finalize, take } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '../../../material/material.module';
import { GamePageViewModel } from '../../../shared/models/GameModels';
import { GameService } from '../../../shared/services/game.service';
import { ScheduleService } from '../../../shared/services/schedule.service';
import { SetGameFilterDialogComponent } from '../../set-game-filter/set-game-filter-dialog.component';
import { AddToScheduleDialogComponent } from '../../components/add-to-schedule-dialog/add-to-schedule-dialog.component';
import { GroupByDateAndDivisionPipe } from '../../../shared/pipes/group-by-date-and-division.pipe';
import { MatIconModule } from '@angular/material/icon';
import { ImageFallbackDirective } from '../../../shared/directives/image-fallback.directive';



const GAME_LIST3_FILTERS_STORAGE_KEY = 'game-list3-active-filters';

@Component({
  selector: 'app-game-list3',
  imports: [ CommonModule,
        RouterModule,
        TranslateModule,
        MaterialModule,
        MatIconModule,
        GroupByDateAndDivisionPipe,
        ImageFallbackDirective
        ],
  templateUrl: './game-list3.component.html',
  styleUrl: './game-list3.component.css',
})
export class GameList3Component {
  _pageData = signal<GamePageViewModel>({ DivisionFilter: [], Games: [] } as GamePageViewModel);
  pastGamesActive = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  useLocalTimezone = signal<boolean>(true);
  GroupByPropertyName = signal<string>("GameDate");
  sort = signal<'asc' | 'desc'>('asc');
  pendingFilterText = signal<string>('');
  activeFilters = signal<GameFilter[]>([]);

  private nextFilterId = 1;

  constructor(
    public gameService: GameService,
    private scheduleService: ScheduleService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private translate: TranslateService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.GroupByPropertyName.set("GameDate");
    this.restoreSavedFilters();

    const showPast = this.route.snapshot.queryParamMap.get('showPast');
    if (showPast === 'true') {
      this.getPastGames();
    } else {
      this.getData();
    }
  }

  getData() {
    this.isLoading.set(true);
    this.pastGamesActive.set(false);
    this.sort.set('desc');
    this.gameService.getGamesFiltered(this.getRequestFilters()) 
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (data) => {
          this._pageData.set(data);
          // console.log('Game data:', data); // Felsökning
        },
        error: (error) => {
          // console.error('Error loading games:', error);
          this.openSnackBar('Error loading games', '');
        }
      });
      //console.log('Filter' + JSON.stringify(this.activeFilters()));
  }

  getPastGames() {
    this.isLoading.set(true);
    this.pastGamesActive.set(true);
    this.sort.set('asc');
    this.gameService.getPastGamesFiltered(this.getRequestFilters())
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (data) => {
          this._pageData.set(data);
          //console.log('Past game data:', data); // Felsökning
        },
        error: (error) => {
          // console.error('Error loading past games:', error);
          this.openSnackBar('Error loading past games', '');
        }
      });
  }

  openSetGameFilterDialog(): void {
    const dialogRef = this.dialog.open(SetGameFilterDialogComponent, {
      width: '600px'
    });

    dialogRef.componentInstance.divisionChanged
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => {
        this.getData();
      });

    dialogRef.componentInstance.showAllDivisionChanged
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => {
        this.getData();
      });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.getData();
        }
      });
  }

  addToSchedule(GameId: string, HomeTeam: string, AwayTeam: string, GameDate: Date, GameTime: Date) {
    const dialogRef = this.dialog.open(AddToScheduleDialogComponent, {
      width: '350px',
      data: {
        GameId: GameId,
        GameDate: GameDate,
        GameTime: GameTime,
        HomeTeam: HomeTeam,
        AwayTeam: AwayTeam
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        this.openSnackBar("EVENT ADDED", "");
        if (this.pastGamesActive()) {
          this.getPastGames();
        } else {
          this.getData();
        }
      });
  }

  removeFromSchedule(GameId: string) {
    this.openSnackBar("EVENT REMOVED", "");
    this.scheduleService.removeEventFromSchedule(GameId)
      .pipe(take(1))
      .subscribe(() => {
        if (this.pastGamesActive()) {
          this.getPastGames();
        } else {
          this.getData();
        }
      });
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  changeBgColor(gameId: string) {
    const element = document.getElementById(gameId);
    if (element) {
      element.style.backgroundColor = '#f0f0f0';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 200);
    }
  }

  getIconPath(icon: string): string {
    if(icon==null || icon==undefined) {
      return '../assets/img/noimage.png';
    }
    icon = icon.toLowerCase();
    if (icon.startsWith('/')) {
      return `../assets/img${icon}`;
    } else {
      return `../assets/img/${icon}`;
    }
  }
/* 
  handleImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = '../assets/img/placeholder.png';
    imgElement.style.width = '30px';
    imgElement.style.height = '30px';
    imgElement.style.objectFit = 'contain';
  } */

  toggleTimezone(timezone: string) {
    this.GroupByPropertyName.set(timezone);
    this.useLocalTimezone.set(timezone === 'GameDate');
    if (this.pastGamesActive()) {
      this.getPastGames();
    } else {
      this.getData();
    }
  }

  applyTeamFilter() {
    const rawText = this.pendingFilterText().trim();
    if (!rawText) {
      return;
    }

    const teamFilter: GameFilter = {
      id: this.nextFilterId++,
      type: 'team',
      value: rawText
    };

    this.activeFilters.set([...this.activeFilters(), teamFilter]);
    this.persistFilters();
    this.pendingFilterText.set('');

    if (this.pastGamesActive()) {
      this.getPastGames();
    } else {
      this.getData();
    }
  }

  removeFilter(id: number) {
    this.activeFilters.set(this.activeFilters().filter(filter => filter.id !== id));
    this.persistFilters();
    if (this.pastGamesActive()) {
      this.getPastGames();
    } else {
      this.getData();
    }
  }

  clearFilters() {
    this.activeFilters.set([]);
    this.persistFilters();
    if (this.pastGamesActive()) {
      this.getPastGames();
    } else {
      this.getData();
    }
  }

  private getRequestFilters(): GameFilter[] {
    const filters = this.activeFilters();
    const lastFilter = filters[filters.length - 1];
    return lastFilter ? [lastFilter] : [];
  }

  private persistFilters() {
    localStorage.setItem(GAME_LIST3_FILTERS_STORAGE_KEY, JSON.stringify(this.activeFilters()));
  }

  private restoreSavedFilters() {
    const savedFiltersJson = localStorage.getItem(GAME_LIST3_FILTERS_STORAGE_KEY);
    if (!savedFiltersJson) {
      return;
    }

    try {
      const parsed = JSON.parse(savedFiltersJson) as GameFilter[];
      const restored = parsed.filter(filter =>
        !!filter &&
        typeof filter.id === 'number' &&
        filter.type === 'team' &&
        typeof filter.value === 'string' &&
        filter.value.trim().length > 0
      );

      const normalized = restored.map((filter, index) => ({
        id: index + 1,
        type: 'team' as const,
        value: filter.value.trim()
      }));

      this.activeFilters.set(normalized);
      const maxId = normalized.reduce((max, filter) => Math.max(max, filter.id), 0);
      this.nextFilterId = maxId + 1;
    } catch {
      this.activeFilters.set([]);
      this.nextFilterId = 1;
    }
  }
}
